/**
 * Tech Contacts card (CONTACTS-SUBCONTRACT-LAYER.md Prompt E, per the
 * approved mockup mockups/contacts/job-contact-card.html).
 *
 * KNOW-column card fed by GET /api/jobs/:id/contact-card — TWO PEOPLE MAX,
 * EVER: the on-site person (arrival & access) and the customer coordinator
 * (problems & questions), plus the office line as the escape hatch. Techs
 * never see routing, purposes, or the hierarchy — each slot carries a quiet
 * ⓘ that reveals the resolved_from label ("Scheduling contact — 7-Eleven
 * (group)") on tap, nothing more.
 *
 * The ⓘ row also carries the "Wrong number?" flag: one tap files a Work
 * Inbox item for the office ("tech says this number is wrong") through the
 * save-outbox, so it works from a mechanical room with no signal.
 *
 * NotifyCoordinatorCard is the device-fail inline action ("Notify {name}"):
 * call/text goes through the tech's phone (tel:/sms:), and the tap is logged
 * to the job so the office sees the coordinator was told.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Flag, Info, MessageSquare, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { saveThroughOutbox } from "@/lib/save-outbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// ─── Types (mirror src/server/routes/jobContactCard.ts) ─────────────────────

export interface ContactCardSlot {
    contact_id: string | null;
    name: string | null;
    role_title: string | null;
    works_for: "account" | "end_customer" | null;
    phone: string | null;
    email: string | null;
    resolved_from: { source: string; label: string };
}

export interface ContactCardOffice {
    name: string | null;
    phone: string;
    resolved_from: { source: string; label: string };
}

export interface ContactCardData {
    on_site: ContactCardSlot | null;
    coordinator: ContactCardSlot | null;
    office: ContactCardOffice | null;
    site_note: string | null;
}

export function telHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

export function smsHref(phone: string): string {
    return `sms:${phone.replace(/[^\d+]/g, "")}`;
}

function initials(name: string | null): string {
    const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return parts
        .slice(0, 2)
        .map((p) => p[0]!.toUpperCase())
        .join("");
}

/** Shared fetch — the card and the device-fail notify action use one cache. */
export function useJobContactCard(jobId: string | null | undefined) {
    return useQuery({
        queryKey: ["job-contact-card", jobId],
        queryFn: () => api.get<ContactCardData>(`/api/jobs/${jobId}/contact-card`),
        enabled: !!jobId,
        staleTime: 300_000,
    });
}

// ─── Wrong-number flag ───────────────────────────────────────────────────────

async function postWrongNumber(jobId: string, slot: "on_site" | "coordinator", contact: ContactCardSlot) {
    await saveThroughOutbox({
        url: `/api/jobs/${jobId}/contact-card/wrong-number`,
        method: "POST",
        body: {
            contactId: contact.contact_id,
            slot,
            name: contact.name,
            phone: contact.phone,
        },
    });
}

// ─── One person row (at most two of these ever render) ──────────────────────

function PersonRow({
    jobId,
    slot,
    slotKey,
    avatarClassName,
}: {
    jobId: string;
    slot: ContactCardSlot;
    slotKey: "on_site" | "coordinator";
    avatarClassName: string;
}) {
    const [showResolved, setShowResolved] = useState(false);
    const [flagState, setFlagState] = useState<"idle" | "sending" | "flagged">("idle");

    const flagWrongNumber = async () => {
        if (flagState !== "idle" || !slot.contact_id) return;
        setFlagState("sending");
        try {
            // Outbox-first: queued offline still succeeds — the drain sends
            // it when signal comes back, so the confirmation is honest.
            await postWrongNumber(jobId, slotKey, slot);
            setFlagState("flagged");
            toast.success("Flagged — the office will check this number.");
        } catch {
            setFlagState("idle");
            toast.error("Couldn't send the flag — try again.");
        }
    };

    return (
        <div data-testid="contact-person" className="space-y-2">
            <div className="flex items-start gap-3">
                <div
                    className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white",
                        avatarClassName,
                    )}
                >
                    {initials(slot.name)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-semibold leading-tight">{slot.name ?? "Contact on file"}</span>
                        {slot.works_for === "end_customer" && (
                            <Badge
                                variant="outline"
                                className="border-amber-300 bg-amber-50 text-[9px] font-bold tracking-wide text-amber-700 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-400"
                            >
                                WORKS AT THE STORE
                            </Badge>
                        )}
                    </div>
                    {slot.role_title && <div className="text-xs text-foreground/70">{slot.role_title}</div>}
                </div>
                <button
                    type="button"
                    aria-label="Where this contact comes from"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-foreground/40 hover:text-foreground/70"
                    onClick={() => setShowResolved((v) => !v)}
                >
                    <Info className="h-4 w-4" />
                </button>
            </div>

            {showResolved && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/60 px-2.5 py-1.5">
                    <span className="text-xs text-foreground/70">{slot.resolved_from.label}</span>
                    <button
                        type="button"
                        disabled={flagState !== "idle" || !slot.contact_id}
                        onClick={flagWrongNumber}
                        className={cn(
                            "inline-flex min-h-[32px] items-center gap-1 text-xs font-semibold",
                            flagState === "flagged" ? "text-green-700 dark:text-green-400" : "text-primary",
                        )}
                    >
                        <Flag className="h-3 w-3" />
                        {flagState === "flagged"
                            ? "Flagged for office"
                            : flagState === "sending"
                              ? "Flagging…"
                              : "Wrong number?"}
                    </button>
                </div>
            )}

            {slot.phone && (
                <div className="grid grid-cols-2 gap-2">
                    <Button asChild variant="outline" size="lg" className="h-12">
                        <a href={telHref(slot.phone)}>
                            <Phone className="mr-2 h-4 w-4" /> Call
                        </a>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="h-12">
                        <a href={smsHref(slot.phone)}>
                            <MessageSquare className="mr-2 h-4 w-4" /> Text
                        </a>
                    </Button>
                </div>
            )}
        </div>
    );
}

// ─── The KNOW-column card ────────────────────────────────────────────────────

export function ContactsCardView({ jobId, data }: { jobId: string; data: ContactCardData }) {
    const { on_site, coordinator, office, site_note } = data;
    if (!on_site && !coordinator && !office) return null;

    return (
        <Card data-testid="contacts-card">
            <CardContent className="space-y-4 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Contacts</div>

                {on_site && (
                    <div className="space-y-2">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            On-site — arrival &amp; access
                        </div>
                        <PersonRow jobId={jobId} slot={on_site} slotKey="on_site" avatarClassName="bg-orange-800" />
                        {site_note && (
                            <div className="rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs leading-snug text-foreground/80 dark:border-amber-500/40 dark:bg-amber-500/10">
                                <span className="font-semibold">Site notes:</span> {site_note}
                            </div>
                        )}
                    </div>
                )}

                {coordinator && (
                    <div className={cn("space-y-2", on_site && "border-t pt-3")}>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            Problem or question — the customer
                        </div>
                        <PersonRow
                            jobId={jobId}
                            slot={coordinator}
                            slotKey="coordinator"
                            avatarClassName="bg-cyan-700"
                        />
                    </div>
                )}

                {office && (
                    <div className={cn("flex items-center gap-2.5", (on_site || coordinator) && "border-t pt-3")}>
                        <Building2 className="h-4 w-4 shrink-0 text-foreground/60" />
                        <span className="min-w-0 flex-1 text-xs text-foreground/70">
                            Schedule changes, or can&apos;t reach anyone? That&apos;s the office.
                        </span>
                        <a
                            href={telHref(office.phone)}
                            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 text-sm font-semibold text-primary"
                        >
                            <Phone className="h-4 w-4" /> Call office
                        </a>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function ContactsCard({ jobId }: { jobId: string }) {
    const q = useJobContactCard(jobId);
    if (!q.data) return null;
    return <ContactsCardView jobId={jobId} data={q.data} />;
}

// ─── Device-fail inline action: Notify {coordinator} ────────────────────────

export function NotifyCoordinatorCard({ jobId }: { jobId: string }) {
    const q = useJobContactCard(jobId);
    const coordinator = q.data?.coordinator ?? null;
    if (!coordinator?.phone) return null;

    const logNotify = (channel: "call" | "text") => {
        // Fire-and-forget through the outbox — the tel:/sms: link opens the
        // phone app regardless; the log lands when signal allows.
        saveThroughOutbox({
            url: `/api/jobs/${jobId}/contact-card/notify-coordinator`,
            method: "POST",
            body: {
                channel,
                contactId: coordinator.contact_id,
                name: coordinator.name,
                phone: coordinator.phone,
            },
        }).catch(() => {});
        toast.success(`Logged — the office knows ${coordinator.name ?? "the coordinator"} was notified.`);
    };

    const first = (coordinator.name ?? "the coordinator").split(/\s+/)[0];
    return (
        <Card data-testid="notify-coordinator" className="border-amber-300 dark:border-amber-500/60">
            <CardContent className="space-y-2.5 p-4">
                <div className="text-sm font-semibold">
                    Device failed — notify {coordinator.name ?? "the coordinator"}
                </div>
                {coordinator.role_title && <div className="text-xs text-foreground/70">{coordinator.role_title}</div>}
                <div className="grid grid-cols-2 gap-2">
                    <Button asChild size="lg" className="h-12">
                        <a href={telHref(coordinator.phone)} onClick={() => logNotify("call")}>
                            <Phone className="mr-2 h-4 w-4" /> Call {first}
                        </a>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="h-12">
                        <a href={smsHref(coordinator.phone)} onClick={() => logNotify("text")}>
                            <MessageSquare className="mr-2 h-4 w-4" /> Text {first}
                        </a>
                    </Button>
                </div>
                <p className="text-xs text-foreground/60">
                    No prices or billing on site — the office sends the customer the numbers.
                </p>
            </CardContent>
        </Card>
    );
}
